import { formatDate, formatInteger, formatTime } from '@brimo/shared';
import type { HomeAppointmentStatus, HomeData, HomeLifecycleGroup } from './api';

/**
 * German texts of the start page (Nico feedback 28.10.2026, design approved 29.10.2026; D-327…D-331). Pure functions,
 * so the page and its unit tests share them.
 */

function plural(count: number, one: string, many: string): string {
  return `${formatInteger(count)} ${count === 1 ? one : many}`;
}

/** Hour of a point in time in Europe/Berlin. */
function berlinHour(at: Date): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Berlin' }).format(
      at,
    ),
  );
}

/** „Guten Morgen" (5–10 Uhr), „Guten Tag" (11–17 Uhr), „Guten Abend" (otherwise). */
export function salutation(at: Date): string {
  const hour = berlinHour(at);
  if (hour >= 5 && hour < 11) return 'Guten Morgen';
  if (hour >= 11 && hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

/** „Guten Morgen, Jonas." */
export function greetingTitle(firstName: string, at: Date): string {
  const name = firstName.trim();
  return name === '' ? `${salutation(at)}.` : `${salutation(at)}, ${name}.`;
}

/** „Hallmayr Immobilien · Donnerstag, 29.10.2026" (the day is the server's Berlin day). */
export function greetingEyebrow(organizationName: string, today: string): string {
  const [year = 1970, month = 1, day = 1] = today.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'long', timeZone: 'Europe/Berlin' }).format(
    date,
  );
  return `${organizationName} · ${weekday}, ${formatDate(date)}`;
}

/** „3 Termine heute, 1 Aufgabe überfällig, 12 neue Leads seit letzter Woche." — mobile: „3 Termine heute · 1 Aufgabe überfällig". */
export function greetingSentence(greeting: HomeData['greeting'], mobile: boolean): string {
  const appointments = `${plural(greeting.appointmentsToday, 'Termin', 'Termine')} heute`;
  const overdue = `${plural(greeting.overdueTasks, 'Aufgabe', 'Aufgaben')} überfällig`;
  if (mobile) return `${appointments} · ${overdue}`;
  const leads = `${plural(greeting.newLeads7d, 'neuer Lead', 'neue Leads')} seit letzter Woche`;
  return `${appointments}, ${overdue}, ${leads}.`;
}

/** Delta of „Termine heute": „nächster 14:30 Uhr · Brunnthaler" / „keine weiteren heute". */
export function nextAppointmentText(next: HomeData['kpis']['appointmentsToday']['next']): string {
  return next === null ? 'keine weiteren heute' : `nächster ${formatTime(next.at)} Uhr · ${next.lastName}`;
}

/** Delta of „Neue Leads · 7 Tage": „+4 gegenüber Vorwoche" / „−2 gegenüber Vorwoche" / „wie Vorwoche". */
export function leadsDelta(delta: number): { text: string; tone: 'neutral' | 'positive' | 'negative' } {
  if (delta === 0) return { text: 'wie Vorwoche', tone: 'neutral' };
  if (delta > 0) return { text: `+${formatInteger(delta)} gegenüber Vorwoche`, tone: 'positive' };
  return { text: `\u2212${formatInteger(-delta)} gegenüber Vorwoche`, tone: 'negative' };
}

/** Delta of „Pipeline-Volumen": „8 offene Deals · 2 reserviert". */
export function pipelineDelta(openDeals: number, reserved: number): string {
  return `${plural(openDeals, 'offener Deal', 'offene Deals')} · ${formatInteger(reserved)} reserviert`;
}

export const areaTitles = {
  contacts: 'Kunden',
  properties: 'Objekte',
  deals: 'Deals',
  calendar: 'Kalender',
  dialer: 'Dialer',
  controlling: 'Auswertung',
} as const;

export const appointmentStatusPills: Record<
  HomeAppointmentStatus,
  { label: string; variant: 'success' | 'info' | 'danger' | 'neutral' }
> = {
  attended: { label: 'wahrgenommen', variant: 'success' },
  booked: { label: 'geplant', variant: 'info' },
  no_show: { label: 'No-Show', variant: 'danger' },
  cancelled: { label: 'storniert', variant: 'neutral' },
};

export const lifecycleChipVariants: Record<
  HomeLifecycleGroup,
  'info' | 'warning' | 'accent' | 'success' | 'danger' | 'neutral'
> = {
  new: 'info',
  call_booked: 'warning',
  follow_up: 'accent',
  prospect: 'info',
  customer: 'success',
  no_show: 'danger',
  deferred: 'neutral',
};
